import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { BELTS } from '../../data/belts';
import { useDojangs } from '../../hooks/useDojangs';

const { FiX, FiCheck } = FiIcons;


export default function UserForm({ user, onClose, onSave }) {
  const { dojangs } = useDojangs();
  const [formData, setFormData] = useState(user ? { ...user, password: '' } : {
    name: '', email: '', password: '', phone: '', birthDate: '', tkdStartDate: '', belt: 'blanca', dojang: 'inteq', dojangOther: '', roles: ['estudiante']
  });

  useEffect(() => {
    // Bloquear scroll del body
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const toggleRole = (role) => {
    const newRoles = formData.roles.includes(role)
      ? formData.roles.filter(r => r !== role)
      : [...formData.roles, role];
    setFormData({ ...formData, roles: newRoles });
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-start justify-center overflow-y-auto bg-zinc-950/95 backdrop-blur-xl py-10 px-4">
      {/* Overlay para cerrar al hacer click fuera */}
      <div className="fixed inset-0 -z-10" onClick={onClose} />
      
      <motion.div 
        initial={{ scale: 0.95, opacity: 0, y: 20 }} 
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="glass-modal w-full max-w-2xl rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/5">
          <h2 className="text-2xl font-black uppercase tracking-tighter text-white">
            {user ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-red-600 rounded-xl text-white transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="p-8 space-y-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Nombre Completo *</label>
              <input 
                required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm text-white focus:border-red-500 transition-colors outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Correo Electrónico *</label>
              <input 
                type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm text-white focus:border-red-500 transition-colors outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">{user ? 'Nueva contraseña' : 'Contraseña *'}</label>
              <input 
                type="text" required={!user} minLength={6} autoComplete="new-password"
                placeholder={user ? 'Dejar vacío para no cambiarla' : 'Mínimo 6 caracteres'}
                value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm text-white focus:border-red-500 transition-colors outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Teléfono</label>
              <input 
                value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm text-white focus:border-red-500 transition-colors outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Grado TKD</label>
              <select 
                value={formData.belt} onChange={e => setFormData({...formData, belt: e.target.value})}
                className="w-full bg-zinc-900 border border-white/10 rounded-2xl p-4 text-sm text-white appearance-none focus:border-red-500 transition-colors outline-none"
              >
                {BELTS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Dojang</label>
              <select
                value={formData.dojang} onChange={e => setFormData({...formData, dojang: e.target.value})}
                className="w-full bg-zinc-900 border border-white/10 rounded-2xl p-4 text-sm text-white appearance-none focus:border-red-500 transition-colors outline-none"
              >
                {dojangs.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>
            {formData.dojang === 'otro' && (
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Nombre del dojang</label>
                <input
                  required maxLength={80} value={formData.dojangOther || ''} onChange={e => setFormData({...formData, dojangOther: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm text-white focus:border-red-500 transition-colors outline-none"
                />
              </div>
            )}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Fecha de nacimiento</label>
              <input 
                type="date" min="1900-01-01" max={new Date().toLocaleDateString('en-CA')} value={formData.birthDate} onChange={e => setFormData({...formData, birthDate: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm text-white focus:border-red-500 transition-colors outline-none [color-scheme:dark]"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Fecha de ingreso al TKD</label>
              <input 
                type="date" min={formData.birthDate || '1900-01-01'} max={new Date().toLocaleDateString('en-CA')} value={formData.tkdStartDate} onChange={e => setFormData({...formData, tkdStartDate: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm text-white focus:border-red-500 transition-colors outline-none [color-scheme:dark]"
              />
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-[10px] font-black uppercase text-zinc-500 ml-2 tracking-widest">Roles de Acceso</label>
            <div className="flex flex-wrap gap-6">
              {['admin', 'profesor', 'estudiante'].map(role => (
                <label key={role} className="flex items-center gap-3 cursor-pointer group">
                  <div 
                    onClick={() => toggleRole(role)}
                    className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.roles.includes(role) ? 'bg-red-600 border-red-600' : 'border-white/10 bg-white/5'}`}
                  >
                    {formData.roles.includes(role) && <SafeIcon icon={FiCheck} className="text-white w-4 h-4" />}
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-white/60 group-hover:text-white">{role}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button 
              type="button"
              onClick={onClose}
              className="flex-1 bg-white/5 hover:bg-white/10 text-white py-5 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all border border-white/5"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="flex-[2] bg-red-600 hover:bg-red-500 text-white py-5 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all shadow-xl shadow-red-600/20"
            >
              Guardar Cambios
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}